import React, { memo, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useEntitlements } from '../contexts/EntitlementContext';
import PaymentService from '../services/payment';
import { TIERS, planProductKey } from '../services/subscription';

const PLANS = {
  [TIERS.PREMIUM]: {
    name: 'Premium',
    monthlyPrice: '£1.99',
    annualPrice: '£13.99',
    annualSavings: '40%',
    features: [
      { label: 'Ad-free experience', included: true },
      { label: '4 bonus themes', included: true },
      { label: '3 daily Undos + 1 Bomb + 1 Refresh', included: true },
      { label: 'Weekly challenges', included: false },
      { label: 'Exclusive effects', included: false },
      { label: 'All 8 themes', included: false },
    ],
    color: '#4D96FF',
  },
  [TIERS.PREMIUM_PLUS]: {
    name: 'Premium+',
    monthlyPrice: '£3.99',
    annualPrice: '£27.99',
    annualSavings: '40%',
    features: [
      { label: 'Ad-free experience', included: true },
      { label: '4 bonus themes', included: true },
      { label: '5 daily Undos + 3 Bombs + 3 Refreshes', included: true },
      { label: 'Weekly challenges', included: true },
      { label: 'Exclusive effects', included: true },
      { label: 'All 8 themes', included: true },
    ],
    color: '#BB86FC',
  },
};

function SubscriptionScreen({ onBack, onManageSubscription }) {
  const { isPremium, isPremiumPlus } = useEntitlements();
  const [annual, setAnnual] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const handlePurchase = useCallback(async (tierKey) => {
    setPurchasing(true);
    try {
      const productKey = planProductKey(tierKey, annual ? 'annual' : 'monthly');
      const result = await PaymentService.purchase(productKey);
      if (!result?.success && !result?.canceled) {
        toast.error(result?.error || 'Purchase failed');
      }
    } finally {
      setPurchasing(false);
    }
  }, [annual]);

  const handleManage = useCallback(async () => {
    const result = await PaymentService.manageSubscription();
    if (!result?.success) {
      toast.error(result?.error || 'Could not open subscription management');
    }
  }, []);

  const handleRestore = useCallback(async () => {
    const result = await PaymentService.restorePurchases();
    if (!result?.success) {
      toast.error(result?.error || 'Restore failed');
    } else if (result?.message) {
      toast.success(result.message);
    }
  }, []);

  const isSubscribed = isPremium || isPremiumPlus;

  return (
    <div className="subscription-screen">
      <div className="subscription-card">
        <div className="subscription-header">
          <button className="back-btn" onClick={onBack} aria-label="Back">
            &#8592;
          </button>
          <h2 className="subscription-title">GO PREMIUM</h2>
        </div>

        {isSubscribed && (
          <div className="subscription-current">
            <span className="subscription-current-badge">
              {isPremiumPlus ? 'PREMIUM+' : 'PREMIUM'}
            </span>
            <span className="subscription-current-text">Active subscription</span>
            <button className="subscription-manage-btn" onClick={handleManage}>
              Manage Subscription
            </button>
          </div>
        )}

        {!isSubscribed && (
          <>
            <div className="billing-toggle">
              <button
                className={`toggle-btn${!annual ? ' toggle-active' : ''}`}
                onClick={() => setAnnual(false)}
              >
                Monthly
              </button>
              <button
                className={`toggle-btn${annual ? ' toggle-active' : ''}`}
                onClick={() => setAnnual(true)}
              >
                Annual
                <span className="toggle-savings">Save 40%</span>
              </button>
            </div>

            <div className="plan-cards">
              {Object.entries(PLANS).map(([key, plan]) => (
                <div
                  key={key}
                  className={`plan-card${key === 'premium_plus' ? ' plan-card-featured' : ''}`}
                  style={{ '--plan-color': plan.color }}
                >
                  {key === 'premium_plus' && (
                    <div className="plan-badge">BEST VALUE</div>
                  )}
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">
                    <span className="plan-price-amount">
                      {annual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="plan-price-interval">
                      /{annual ? 'year' : 'month'}
                    </span>
                  </div>

                  <ul className="plan-features">
                    {plan.features.map((feature, i) => (
                      <li
                        key={i}
                        className={`plan-feature${feature.included ? ' plan-feature-included' : ' plan-feature-excluded'}`}
                      >
                        <span className="plan-feature-icon">
                          {feature.included ? '\u2713' : '\u2717'}
                        </span>
                        {feature.label}
                      </li>
                    ))}
                  </ul>

                  <button
                    className="plan-subscribe-btn"
                    onClick={() => handlePurchase(key)}
                    disabled={purchasing}
                    style={{ background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)` }}
                  >
                    {purchasing ? 'Processing...' : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {!isSubscribed && (
          <p className="subscription-disclosure">
            Subscriptions auto-renew until cancelled. Manage or cancel anytime in your{' '}
            {PaymentService.getPlatform() === 'ios' ? 'iOS Settings → Apple ID → Subscriptions' : 'account portal'}.
            {' '}
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Use (EULA)</a>
            {' · '}
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </p>
        )}

        {PaymentService.getPlatform() === 'ios' && (
          <button className="restore-link" onClick={handleRestore}>
            Restore Purchases
          </button>
        )}
      </div>
    </div>
  );
}

export default memo(SubscriptionScreen);
